import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { measureTypeDefinition } from '../../../../entity-definitions/measure-type.definition';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { ToastService } from '../../../../services/toast.service';
import { MeasureTypeStore } from '../../../../stores/measure-type.store';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { isEntityFormDeleteAction } from '../../../../types/entity-form-result.interface';
import { MeasureType } from '../../../../types/measure-type.interface';
import { GenericParentGridComponent } from '../../../generic/generic-parent-grid.component';
import { CesButtonComponent } from '../../../ui/ces-buttons';
import { CesIconComponent } from '../../../ui/ces-icon/ces-icon.component';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

@Component({
  selector: 'app-measure-types-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesTitleComponent, GenericParentGridComponent, CesButtonComponent, CesIconComponent, TranslocoModule],
  templateUrl: './measure-types-list.component.html',
})
export class MeasureTypesListComponent implements OnInit {
  protected readonly measureTypeStore = inject(MeasureTypeStore);
  private readonly dialogService = inject(EntityDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  measureTypeDefinition = measureTypeDefinition;
  measureTypes: MeasureType[] = [];

  dataLoadingCompleted: boolean = false;
  loadFailed: boolean = false;

  ngOnInit(): void {
    void this.loadData();
  }

  async loadData(): Promise<void> {
    this.loadFailed = false;
    try {
      await this.measureTypeStore.loadAll();
      this.measureTypes = this.measureTypeStore.entities();
      this.dataLoadingCompleted = true;
    } catch (error) {
      this.loadFailed = true;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  createMeasureType(): void {
    this.dialogService
      .openCreateDialog<MeasureType>({
        header: this.measureTypeDefinition.messages.dialogTitles.create(),
        entityDefinition: this.measureTypeDefinition,
        mode: 'create',
      })
      .subscribe(formData => {
        if (formData && !isEntityFormDeleteAction(formData)) {
          void (async () => {
            try {
              await this.measureTypeStore.create(formData);
              await this.loadData();
            } catch (error) {
              console.error(this.translateService.translate('common.errors.measureTypeCreationFailed'), error);
            }
          })();
        }
      });
  }

  editMeasureType(measureType: MeasureType): void {
    this.dialogService
      .openEditDialog<MeasureType>(measureType, {
        header: this.measureTypeDefinition.messages.dialogTitles.edit(),
        headerParams: { name: measureType.name },
        entityDefinition: this.measureTypeDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteMeasureType(entity),
      })
      .subscribe(result => {
        if (result) {
          if (isEntityFormDeleteAction(result)) {
            this.deleteMeasureType(result.entity);
          } else {
            void this.measureTypeStore.update(measureType.id, result as Partial<MeasureType>).then(() => {
              void this.loadData();
            });
          }
        }
      });
  }

  deleteMeasureType(measureType: MeasureType): void {
    if (measureType.isUsed) {
      this.toastService.warning('common.errors.cannotDeleteUsedMeasureType');
      return;
    }

    void this.measureTypeStore
      .delete(measureType.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.measureType.messages.deleteError');
        console.error(this.translateService.translate('common.errors.measureTypeDeletionFailed'), error);
      });
  }
}
